/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { SET_LOADING, RESET_LOADING } from '../store/state/actions';
import { dispatch } from '../store/state/state-manager';

/**
 * The <strong>IsLoading Manager</strong> object provides access to methods
 * that are used to indicate a need to trigger a Loading indicator...
 */

class IsLoadingManager {
  /**
   * Constructor - <strong>Not for use outside of Core! Included for reference only.</strong>
   */
  private waitingToPutUpIndicator: boolean;
  private contextArray: Record<string, number>;
  private readonly pauseDuration: number;

  constructor() {
    // the contextArray stores an associative array where the keys are "contexts" for which a possible
    //  need for a loading indicator has been noted (ex: "app", "app/primary_5") and the value is the
    //  current number of pending requests for this context are still in play. When all of the interest
    //  in a given context has been resolved (ex: all of the promises have been settled), this value will
    //  be 0.
    this.contextArray = {};
    this.pauseDuration = 2000; // milliseconds
    this.waitingToPutUpIndicator = false;
  }

  /**
   * Returns the context of interest to the IsLoading indicator (ex: "app", "app/primary_5") found in the given payload
   * @param thePayload the Payload where the context can be found
   * @returns the context of interest for the given payload. Returns null if not found
   */

  getLoadingContext(thePayload: any): string | null {
    let context: string | null = null;
    if (thePayload) {
      ({ context } = thePayload);
      if (!context) {
        // Containers store the "context" (ex: "app/primary") as the "containerItemID"
        context = thePayload.containerItemID;
      }
      if (!context) {
        // Pulse actions store the "context" (ex: "app/primary") as the "contextName"
        context = thePayload.contextName;
      }
    }
    return context;
  }

  /**
   * Notes the possible need for a loading indicator for the "context" specified in the payload
   * @param actionPayload the associated payload. It is assumed that this payload has a "context"
   * (ex: "app", "app/primary_5") that can be found and used as the target of the loading indicator
   * @param allowDispatch If true (default), the implementation can call dispatch actions.
   * If false, do not call dispatch actions (typically because this is being called in the context of a reducer where
   * calls to actions are not allowed)
   */
  enableLoadingIndicator(actionPayload: any, allowDispatch = true) {
    const context = this.getLoadingContext(actionPayload);
    if (!context) {
      return;
    }
    this.addToContextArray(context);
    if (!this.waitingToPutUpIndicator) {
      this.waitingToPutUpIndicator = true;
      this.pauseBeforeShowing(context).then((contextName) => {
        if (allowDispatch && this.contextArray[contextName] !== undefined && this.contextArray[contextName] > 0) {
          dispatch({ type: SET_LOADING, payload: { context: contextName } });
        }
      });
    }
  }

  /**
   * Start a promise that will resolve in this.pauseDuration milliseconds
   * @param context the context (ex: "app", "app/primary_5") for which we want to
   * show a loading indicator after the delay if there's still interest in it
   * @private
   */
  pauseBeforeShowing = (context: string): Promise<string> => {
    return new Promise((resolve) => {
      setTimeout(() => resolve(context), this.pauseDuration);
    });
  };

  /**
   * Notes that an earlier note of a possible need for a loading indicator for the "context" specified
   * in the payload is no longer needed
   * @param actionPayload the associated payload. It is assumed that this payload has a "context"
   * (ex: "app", "app/primary_5") that can be found and used as the target of the loading indicator
   * @param allowDispatch If true (default), the implementation can call dispatch actions.
   * If false, do not call dispatch actions (typically because this is being called in the context of a reducer where
   * calls to actions are not allowed)
   */
  disableLoadingIndicator(actionPayload: any, allowDispatch = true) {
    const context = this.getLoadingContext(actionPayload);

    if (!context) {
      return;
    }
    this.waitingToPutUpIndicator = false;
    this.removeFromContextArray(context);
    if (allowDispatch && this.contextArray[context] !== undefined && this.contextArray[context] === 0) {
      dispatch({ type: RESET_LOADING, payload: { context } });
    }
  }

  /**
   * Adds and/or increments a context entry to contextArray
   * @param context the context to be added (ex: "app", "app/primary_5")
   * @private
   */
  addToContextArray = (context: string) => {
    if (this.contextArray[context] === undefined) {
      // if no entry for this context yet, create and initialize value to 0
      this.contextArray[context] = 0;
    }
    // increment value to show additional interest in this context
    this.contextArray[context] += 1;
  };

  /**
   * Decrements a context entry to contextArray (and, if decremented to 0, remove the entry)
   * @param context the context to be added (ex: "app", "app/primary_5")
   * @private
   */
  removeFromContextArray = (context: string) => {
    // decrement value if it exists to show reduced interest in this context (but never go < 0)
    if (this.contextArray[context] !== undefined && this.contextArray[context] > 0) {
      this.contextArray[context] -= 1;
    }
  };
}

const IsLoadingManagerInstance = new IsLoadingManager();

// Expose IsLoadingManagerInstance singleton so Core, Nebula, and/or other bridges
//  can use these APIs

export { IsLoadingManagerInstance };
